import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { message } from "antd";
import {
  ArrowLeft,
  Calendar,
  Clock3,
  Eye,
  Heart,
  MessageCircle,
  Send,
  Share2,
  Tag,
  UserRound,
} from "lucide-react";
import Avatar from "react-avatar";
import { UserDataContext } from "../context/User";
import { timeAgo } from "../utilis/Time";
import BannerImg from "../image/backgroundbanner.png";

const API_BASE_URL = "";

function stripHtml(value = "") {
  return value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function getEndpoint(type) {
  if (type === "workshops") return "workshop";
  if (type === "tutorials") return "tutorial";
  return "blog";
}

function getResultKey(type) {
  if (type === "workshops") return "workshop";
  if (type === "tutorials") return "tutorial";
  return "blog";
}

function getAuthorName(author) {
  if (!author) return "LWBL Member";
  const fullName = [author.firstName, author.lastName].filter(Boolean).join(" ");
  return author.preferredName || fullName || author.memberId || "LWBL Member";
}

function ContentDetail() {
  const { type, slug } = useParams();
  const resolvedType = type || "blogs";
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(UserDataContext);
  const [messageApi, contextHolder] = message.useMessage();

  const [content, setContent] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fromTheme = location.state?.fromTheme;
  const isBlog = resolvedType === "blogs";
  const blogId = content?._id;
  const author = content?.author;
  const authorName = getAuthorName(author);
  const plainText = useMemo(() => stripHtml(content?.content || ""), [content?.content]);
  const readMinutes = Math.max(1, Math.ceil(plainText.split(/\s+/).filter(Boolean).length / 220));

  const fetchComments = useCallback(
    async (targetBlogId) => {
      if (!targetBlogId || !isBlog) return;

      try {
        setCommentsLoading(true);
        const response = await fetch(`/api/comments/fetch/blog/${targetBlogId}`);
        const result = await response.json();
        setComments(result.success && Array.isArray(result.comments) ? result.comments : []);
        setCommentTotal(result.success ? result.total || result.comments?.length || 0 : 0);
      } catch (err) {
        console.error("Comment fetch error:", err);
        messageApi.error("Unable to load comments");
      } finally {
        setCommentsLoading(false);
      }
    },
    [isBlog, messageApi]
  );

  useEffect(() => {
    let isMounted = true;

    async function fetchContentDetail() {
      try {
        setLoading(true);
        setError(null);

        const endpoint = getEndpoint(resolvedType);
        const response = await fetch(`/api/${endpoint}/fetch/slug/${slug}`, {
          credentials: "include",
        });
        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || "Content not found");
        }

        const data = result[getResultKey(resolvedType)];
        if (!data) {
          throw new Error("Content not found");
        }

        if (!isMounted) return;
        setContent(data);

        if (resolvedType === "blogs") {
          await fetchComments(data._id);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Fetch error:", err);
        setError(err.message || "Failed to load content");
        messageApi.error("Error loading content");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchContentDetail();

    return () => {
      isMounted = false;
    };
  }, [fetchComments, messageApi, resolvedType, slug]);

  const handleBack = () => {
    if (fromTheme) {
      navigate(`/blogs/theme/${fromTheme.slug}`, {
        state: { themeName: fromTheme.name },
      });
      return;
    }

    navigate("/blogs");
  };

  const handleSubmitComment = async (event) => {
    event.preventDefault();

    if (!userData?.id) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }

    if (!commentText.trim()) {
      messageApi.warning("Write a comment before submitting");
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch("/api/comments/create", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: commentText.trim(),
          blogId,
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to add comment");
      }

      setCommentText("");
      setComments((current) => [result.comment, ...current]);
      setCommentTotal((current) => current + 1);
      setContent((current) => ({
        ...current,
        commentsCount: (current?.commentsCount || 0) + 1,
      }));
      messageApi.success("Comment added");
    } catch (err) {
      messageApi.error(err.message || "Unable to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeBlog = async () => {
    if (!userData?.id) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }

    try {
      const response = await fetch(`/api/blog/like/${blogId}`, {
        method: "POST",
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to update like");
      }

      setContent((current) => ({
        ...current,
        likesCount: result.likesCount,
        hasLiked: result.hasLiked,
      }));
      messageApi.success(result.hasLiked ? "Added to liked blogs" : "Like removed");
    } catch (err) {
      messageApi.error(err.message || "Unable to update like");
    }
  };

  const handleShareBlog = async () => {
    const shareUrl = `${window.location.origin}/lwbl/blogs/${content.slug}`;

    try {
      const response = await fetch(`/api/blog/share/${blogId}`, {
        method: "POST",
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to share blog");
      }

      setContent((current) => ({
        ...current,
        sharesCount: result.sharesCount,
      }));

      if (navigator.share) {
        await navigator.share({
          title: content.title,
          text: content.description,
          url: shareUrl,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        messageApi.success("Share link copied");
      } else {
        messageApi.success("Share counted");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        messageApi.error(err.message || "Unable to share blog");
      }
    }
  };

  const commentsSection = isBlog ? (
    <section className="mt-8 rounded-lg border border-[#dce6e6] bg-white p-5 shadow-sm md:p-7">
      <div className="flex flex-col gap-4 border-b border-[#edf2f2] pb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-outfit text-xs uppercase tracking-[0.18em] text-[#00A6C8]">Discussion</p>
          <h2 className="mt-1 flex items-center gap-2 font-cinzel text-2xl text-[#101617]">
            <MessageCircle size={24} />
            Comments ({commentTotal})
          </h2>
        </div>
        {!userData?.id && (
          <button
            type="button"
            onClick={() => navigate("/login", { state: { from: location.pathname } })}
            className="rounded-full border border-[#c7d4d5] px-5 py-2 font-outfit text-sm font-bold text-[#101617] hover:border-[#00C3FF]"
          >
            Sign in to comment
          </button>
        )}
      </div>

      <form onSubmit={handleSubmitComment} className="mt-6">
        <label htmlFor="comment" className="sr-only">Add a comment</label>
        <textarea
          id="comment"
          value={commentText}
          onChange={(event) => setCommentText(event.target.value)}
          rows={4}
          disabled={!userData?.id || submitting}
          placeholder={userData?.id ? "Share a thoughtful response..." : "Sign in to join the discussion"}
          className="w-full resize-none rounded-lg border border-[#d6dfdf] bg-[#fbfdfd] p-4 font-outfit text-sm text-[#101617] outline-none transition focus:border-[#00C3FF] focus:bg-white disabled:cursor-not-allowed disabled:bg-[#f1f4f4]"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="font-outfit text-xs text-[#728080]">
            Keep feedback constructive and relevant to the artist's post.
          </p>
          <button
            type="submit"
            disabled={!userData?.id || submitting}
            className="inline-flex items-center gap-2 rounded-full bg-[#00F0FB] px-5 py-2.5 font-outfit text-sm font-bold text-black transition hover:bg-[#00d8e3] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send size={16} />
            {submitting ? "Posting..." : "Post Comment"}
          </button>
        </div>
      </form>

      <div className="mt-7 space-y-4">
        {commentsLoading && (
          <div className="rounded-lg bg-[#f5f8f8] p-5 font-outfit text-sm text-[#667273]">Loading comments...</div>
        )}

        {!commentsLoading && comments.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#c7d4d5] bg-[#fbfdfd] p-7 text-center">
            <MessageCircle className="mx-auto text-[#00A6C8]" size={30} />
            <h3 className="mt-3 font-cinzel text-xl text-[#101617]">No comments yet</h3>
            <p className="mt-2 font-outfit text-sm text-[#667273]">
              Start the discussion with a question, reflection, or note of appreciation.
            </p>
          </div>
        )}

        {!commentsLoading &&
          comments.map((item) => {
            const commentAuthor = getAuthorName(item.author);
            return (
              <article key={item._id} className="rounded-lg border border-[#edf2f2] bg-[#fbfdfd] p-4">
                <div className="flex items-start gap-3">
                  <Avatar src={item.author?.pfp} name={commentAuthor} size="44" round />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h3 className="font-outfit text-sm font-bold text-[#101617]">{commentAuthor}</h3>
                      <span className="font-outfit text-xs text-[#728080]">{timeAgo(item.createdAt)}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-line font-outfit text-sm leading-6 text-[#374344]">
                      {item.content}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
      </div>
    </section>
  ) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f8f8] flex items-center justify-center">
        <div className="h-14 w-14 rounded-full border-4 border-[#00C3FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-[#f5f8f8] flex items-center justify-center px-4">
        {contextHolder}
        <div className="max-w-md rounded-lg border border-[#dce6e6] bg-white p-8 text-center shadow-sm">
          <h2 className="font-cinzel text-3xl text-[#101617]">Content Not Found</h2>
          <p className="font-outfit text-[#5f6c6d] mt-3">
            The content you are looking for does not exist or has been removed.
          </p>
          <button
            type="button"
            onClick={() => navigate("/blogs")}
            className="mt-6 rounded-full bg-[#00F0FB] px-6 py-3 font-outfit font-bold text-black"
          >
            Back to Blogs
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-[#f5f8f8] text-left"
      >
        <section className="relative min-h-[520px] overflow-hidden bg-[#0f1718] text-white">
          <img
            src={`${API_BASE_URL}${content.coverImage || BannerImg}`}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/30" />

          <div className="relative z-10 mx-auto flex min-h-[520px] max-w-7xl flex-col justify-end px-4 pb-12 pt-8">
            <nav className="mb-auto flex flex-wrap items-center gap-2 pt-6 font-outfit text-sm text-white/75">
              <Link to="/" className="hover:text-[#00F0FB]">Home</Link>
              <span>/</span>
              <Link to="/blogs" className="hover:text-[#00F0FB]">Blogs</Link>
              <span>/</span>
              <span className="text-white">{content.title}</span>
            </nav>

            <button
              type="button"
              onClick={handleBack}
              className="mb-7 inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 font-outfit text-sm font-semibold text-white backdrop-blur hover:border-[#00F0FB] hover:text-[#00F0FB]"
            >
              <ArrowLeft size={17} />
              {fromTheme ? `Back to ${fromTheme.name}` : "Back to Blogs"}
            </button>

            <div className="max-w-4xl">
              {content.genres?.[0]?.name && (
                <span className="inline-flex items-center gap-2 rounded-full bg-[#00F0FB] px-4 py-2 font-outfit text-xs font-bold uppercase tracking-[0.16em] text-black">
                  <Tag size={14} />
                  {content.genres[0].name}
                </span>
              )}
              <h1 className="mt-5 font-cinzel text-4xl leading-tight text-white md:text-6xl">
                {content.title}
              </h1>
              <p className="mt-5 max-w-3xl font-outfit text-base leading-relaxed text-white/86 md:text-lg">
                {content.description}
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <article className="min-w-0">
              <div className="mb-6 rounded-lg border border-[#dce6e6] bg-white p-5 shadow-sm">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="flex items-center gap-3 md:col-span-2">
                    <Avatar
                      src={author?.pfp ? `${API_BASE_URL}${author.pfp}` : undefined}
                      name={authorName}
                      size="52"
                      round
                    />
                    <div>
                      <p className="font-outfit text-xs uppercase tracking-[0.14em] text-[#728080]">Written by</p>
                      <p className="font-outfit text-base font-bold text-[#101617]">{authorName}</p>
                      <p className="font-outfit text-sm text-[#667273]">
                        {author?.memberId ? `Member ID: ${author.memberId}` : "Community contributor"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="text-[#00A6C8]" size={24} />
                    <div>
                      <p className="font-outfit text-xs text-[#728080]">Published</p>
                      <p className="font-outfit text-sm font-bold text-[#101617]">
                        {new Date(content.createdAt).toLocaleDateString("en-US", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock3 className="text-[#00A6C8]" size={24} />
                    <div>
                      <p className="font-outfit text-xs text-[#728080]">Read time</p>
                      <p className="font-outfit text-sm font-bold text-[#101617]">{readMinutes} min read</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[#dce6e6] bg-white p-6 shadow-sm md:p-9">
                <div
                  className="font-outfit text-base leading-8 text-[#263233] prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: content.content }}
                />
              </div>
            </article>

            <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">
              <div className="rounded-lg border border-[#dce6e6] bg-white p-5 shadow-sm">
                <h2 className="font-cinzel text-xl text-[#101617]">Activity</h2>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-[#eef8f8] p-3">
                    <Eye className="mx-auto text-[#00A6C8]" size={18} />
                    <p className="mt-1 font-outfit text-sm font-bold text-[#101617]">{content.viewsCount || 0}</p>
                    <p className="font-outfit text-[11px] text-[#667273]">Views</p>
                  </div>
                  <div className="rounded-lg bg-[#f6f3ee] p-3">
                    <Heart className="mx-auto text-[#b77722]" size={18} />
                    <p className="mt-1 font-outfit text-sm font-bold text-[#101617]">{content.likesCount || 0}</p>
                    <p className="font-outfit text-[11px] text-[#667273]">Likes</p>
                  </div>
                  <div className="rounded-lg bg-[#f2f5fa] p-3">
                    <Share2 className="mx-auto text-[#496da8]" size={18} />
                    <p className="mt-1 font-outfit text-sm font-bold text-[#101617]">{content.sharesCount || 0}</p>
                    <p className="font-outfit text-[11px] text-[#667273]">Shares</p>
                  </div>
                </div>
                {isBlog && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleLikeBlog}
                      className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 font-outfit text-sm font-bold transition ${
                        content.hasLiked
                          ? "border border-[#b4233a] bg-[#ffe7ea] text-[#b4233a] shadow-sm"
                          : "border border-[#d6dfdf] bg-white text-[#101617] hover:border-[#00C3FF] hover:bg-[#eefbfc]"
                      }`}
                      aria-pressed={Boolean(content.hasLiked)}
                    >
                      <Heart size={17} fill={content.hasLiked ? "currentColor" : "none"} />
                      {content.hasLiked ? "Liked" : "Like"}
                    </button>
                    <button
                      type="button"
                      onClick={handleShareBlog}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#00F0FB] px-4 py-3 font-outfit text-sm font-bold text-black transition hover:bg-[#00d8e3]"
                    >
                      <Share2 size={17} />
                      Share
                    </button>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-[#dce6e6] bg-white p-5 shadow-sm">
                <h2 className="flex items-center gap-2 font-cinzel text-xl text-[#101617]">
                  <UserRound size={20} />
                  Author
                </h2>
                <div className="mt-4 flex items-center gap-3">
                  <Avatar
                    src={author?.pfp ? `${API_BASE_URL}${author.pfp}` : undefined}
                    name={authorName}
                    size="48"
                    round
                  />
                  <div>
                    <p className="font-outfit text-sm font-bold text-[#101617]">{authorName}</p>
                    <p className="font-outfit text-xs text-[#667273]">{author?.occupation || author?.memberId || "LWBL member"}</p>
                  </div>
                </div>
              </div>

              {content.genres?.length > 0 && (
                <div className="rounded-lg border border-[#dce6e6] bg-white p-5 shadow-sm">
                  <h2 className="font-cinzel text-xl text-[#101617]">Filed Under</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {content.genres.map((genre) => (
                      <span key={genre._id || genre.name} className="rounded-full bg-[#e9f9fa] px-3 py-1.5 font-outfit text-xs font-bold text-[#007f99]">
                        {genre.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
          {commentsSection}
        </section>
      </motion.main>
    </>
  );
}

export default ContentDetail;
